import moment from 'moment';
import React, { useEffect, useMemo, useReducer, useRef, useState } from 'react';
import { nextSortDirection } from '../../utils/tableSort';
import { Helmet } from 'react-helmet-async';
import { FaFileExcel, FaPlus } from 'react-icons/fa';
import { FiCheck, FiRefreshCw } from 'react-icons/fi';
import { useLocation, useNavigate } from 'react-router-dom';
import { toast } from '../../toasts';
import FilterForm from '../../components/FilterForm';
import Paginator from '../Paginator/Paginator';
import { useLabels } from '../../hooks/useLabels';
import { usePermissions } from '../../hooks/usePermissions';
import ContractService from '../../services/contract.service';
import { getError, showCurrency } from '../../utils';
import { CONTRACT_ESTADO_LABEL, CONTRACT_ESTADO_TONE, contractEstado } from '../../utils/contractEstado';
import {
	EntityList,
	PageHead,
	StatStrip,
	ListToolbar,
	DataTable,
	Button,
	Pill,
	IdChip,
	RowActionsMenu,
	BulkBar,
	ExportModal,
	buildXlsxExport,
} from '../../components/EntityList';
import ContractBulkActionModal from './ContractBulkActionModal';
import { ContractDeletionModal } from '../../components/CascadeDeletionModal';

const reducer = (state, action) => {
	switch (action.type) {
	case 'FETCH_REQUEST':
		return { ...state, loading: true };
	case 'FETCH_SUCCESS':
		return {
			...state,
			contracts: action.payload.contracts,
			page: action.payload.page,
			pages: action.payload.pages,
			countContracts: action.payload.countContracts,
			pageSize: action.payload.pageSize,
			ipc: action.payload.ipc,
			stats: action.payload.stats,
			loading: false,
		};
	case 'FETCH_FAIL':
		return { ...state, loading: false, error: action.payload };
	case 'DELETE_REQUEST':
		return { ...state, loadingDelete: true, successDelete: false };
	case 'DELETE_SUCCESS':
		return {
			...state,
			loadingDelete: false,
			successDelete: true,
		};
	case 'DELETE_FAIL':
		return { ...state, loadingDelete: false, successDelete: false };
	case 'DELETE_RESET':
		return { ...state, loadingDelete: false, successDelete: false };
	case 'UPDATE_REQUEST':
		return { ...state, loadingDelete: true, successDelete: false };
	case 'UPDATE_SUCCESS':
		return {
			...state,
			loadingDelete: false,
			successDelete: true,
		};
	case 'UPDATE_FAIL':
		return { ...state, loadingDelete: false, successDelete: false };
	default:
		return state;
	}
};

function formatDate(date) {
	return date ? moment.utc(date).format('DD-MM-YYYY') : '';
}

function ContractList() {
	const { t } = useLabels();
	const { canSee, canEdit, canCreate, canDelete } = usePermissions();

	const [
		{ loading, error, contracts, successDelete, pageSize, countContracts, ipc, stats },
		dispatch,
	] = useReducer(reducer, {
		contracts: [],
		loading: true,
		error: '',
		pageSize: 0,
		countContracts: 0,
		ipc: null,
		stats: null,
	});
	const initialLoading = loading && pageSize === 0;

	const navigate = useNavigate();
	const { search } = useLocation();
	const sp = new URLSearchParams(search);
	const column = sp.get('column') || 'customId';
	const direction = sp.get('direction') || 'asc';
	const currency = sp.get('currency') || 'all';
	const amountTo = sp.get('amountTo') || '';
	const amountFrom = sp.get('amountFrom') || 0;
	const periodInitFrom = sp.get('periodInitFrom') || '';
	const periodInitTo = sp.get('periodInitTo') || '';
	const periodEndFrom = sp.get('periodEndFrom') || '';
	const periodEndTo = sp.get('periodEndTo') || '';
	const query = sp.get('query') || 'all';
	const page = sp.get('page') || 1;
	const [filtered, setFiltered] = useState(false);
	const [deletingContract, setDeletingContract] = useState(null);
	const [isOpen, setIsOpen] = useState(false);
	const [exportModalOpen, setExportModalOpen] = useState(false);

	const [selectedIds, setSelectedIds] = useState([]);
	const [bulkModalMode, setBulkModalMode] = useState(null); // 'adjust' | 'close' | null

	const handleDropdownToggle = () => {
		setIsOpen(!isOpen);
	};

	const [filterOptions, setFilterOptions] = useState({
		formType: 'Alquileres',
		periodInitFrom: periodInitFrom,
		periodInitTo: periodInitTo,
		periodEndFrom: periodEndFrom,
		periodEndTo: periodEndTo,
		amountFrom: amountFrom,
		amountTo: amountTo,
		currency: currency,
		query: query,
	});

	const handleInputChange = (e) => {
		const { name, value } = e.target;
		setFilterOptions((prevOptions) => ({
			...prevOptions,
			[name]: value,
			formType: filterOptions.formType,
		}));
	};

	// filterOptions seeds from the URL on mount only, while the toolbar search
	// and the paginator navigate without updating it — and getFilterUrl()
	// rebuilds every URL out of it, so a stale value dropped the active search.
	useEffect(() => {
		setFilterOptions({
			formType: 'Alquileres',
			periodInitFrom,
			periodInitTo,
			periodEndFrom,
			periodEndTo,
			amountFrom,
			amountTo,
			currency,
			query,
		});
	}, [periodInitFrom, periodInitTo, periodEndFrom, periodEndTo, amountFrom, amountTo, currency, query]);

	const handleApplyFilter = () => {
		const {
			periodInitFrom,
			periodInitTo,
			periodEndFrom,
			periodEndTo,
			amountFrom,
			amountTo,
			currency,
		} = filterOptions;
		const filter = {
			periodInitFrom,
			periodInitTo,
			periodEndFrom,
			periodEndTo,
			amountFrom,
			amountTo,
			currency,
			page: 1,
			query,
		};
		const filterUrl = getFilterUrl(filter);
		navigate(filterUrl);
		setFiltered(true);
	};
	// Guarded like AccountList: without the ref this fired on mount and
	// immediately re-navigated, wiping column/direction off an incoming
	// (bookmarked or shared) sorted URL and costing a second fetch.
	const isMounted = useRef(false);
	useEffect(() => {
		if (!isMounted.current) {
			isMounted.current = true;
			return;
		}
		handleApplyFilter();
	}, [filtered]);

	const handleClearFilter = () => {
		setFilterOptions({
			formType: 'Alquileres',
			periodInitFrom: '',
			periodInitTo: '',
			periodEndFrom: '',
			periodEndTo: '',
			amountFrom: 0,
			amountTo: '',
			currency: 'all',
			query: 'all',
		});
		setFiltered(false);
	};

	// Toolbar-level "Limpiar" (mirrors AssetList): handleClearFilter only flips
	// `filtered`, and the effect that navigates is keyed on that flag — so when
	// the filters came straight from the URL (filtered still false) nothing
	// happened at all. This navigates immediately instead.
	const handleClearAdvancedFilters = () => {
		setFilterOptions({
			formType: 'Alquileres',
			periodInitFrom: '',
			periodInitTo: '',
			periodEndFrom: '',
			periodEndTo: '',
			amountFrom: 0,
			amountTo: '',
			currency: 'all',
			query: 'all',
		});
		setSelectedIds([]);
		navigate(`/AdminScreen/contracts/search?page=1&query=all&periodInitFrom=&periodInitTo=&periodEndFrom=&periodEndTo=&amountFrom=&amountTo=&currency=all&column=${column}&direction=${direction}`);
	};

	const onPageChange = (page) => {
		setSelectedIds([]);
		navigate(getFilterUrl({ page: page }));
	};

	const handleSort = (sortColumn) => {
		const newSortDirection = nextSortDirection(column, direction, sortColumn);
		const newURL = `/AdminScreen/contracts/search?page=${page}&query=${query}&column=${sortColumn}&direction=${newSortDirection}&currency=${currency}&amountFrom=${amountFrom}&amountTo=${amountTo}&periodInitFrom=${periodInitFrom}&periodInitTo=${periodInitTo}&periodEndFrom=${periodEndFrom}&periodEndTo=${periodEndTo}`;
		navigate(newURL);
	};

	useEffect(() => {
		const fetchData = async () => {
			try {
				dispatch({ type: 'FETCH_REQUEST' });
				const { data } = await ContractService.search(
					`page=${page}&query=${query}&column=${column}&direction=${direction}&currency=${currency}&amountFrom=${amountFrom}&amountTo=${amountTo}&periodInitFrom=${periodInitFrom}&periodInitTo=${periodInitTo}&periodEndFrom=${periodEndFrom}&periodEndTo=${periodEndTo}`,
				);
				dispatch({ type: 'FETCH_SUCCESS', payload: data });
			} catch (err) {
				dispatch({ type: 'FETCH_FAIL', payload: getError(err) });
				console.error(getError(err));
				toast.error(t('msg.listLoadFailed'));
			}
		};
		if (successDelete) {
			dispatch({ type: 'DELETE_RESET' });
		}
		fetchData();
	}, [
		successDelete,
		page,
		direction,
		column,
		periodInitFrom,
		periodInitTo,
		periodEndFrom,
		periodEndTo,
		amountFrom,
		amountTo,
		currency,
		query,
	]);

	// The cascade modal previews everything the delete takes with it.
	function deleteContractHandler(contract) {
		setDeletingContract(contract);
	}

	function editContractHandler(contract) {
		navigate(`/AdminScreen/contracts/editContract/${contract._id}`);
	}

	async function seeDetails(contract) {
		navigate(`/AdminScreen/contracts/contract/${contract._id}`);
	}

	const getFilterUrl = (filter) => {
		const {
			query,
			periodInitFrom,
			periodInitTo,
			periodEndFrom,
			periodEndTo,
			amountFrom,
			amountTo,
			currency,
		} = filterOptions;
		const filterQuery = filter.query || query;
		const filterPage = filter.page || 1;
		const filterPeriodInitFrom = filter.periodInitFrom ?? periodInitFrom ?? '';
		const filterPeriodInitTo = filter.periodInitTo ?? periodInitTo ?? '';
		const filterPeriodEndFrom = filter.periodEndFrom ?? periodEndFrom ?? '';
		const filterPeriodEndTo = filter.periodEndTo ?? periodEndTo ?? '';
		const filterAmountFrom = filter.amountFrom ?? amountFrom ?? '';
		const filterAmountTo = filter.amountTo ?? amountTo ?? '';
		const filterCurrency = filter.currency ?? currency ?? '';

		const params = new URLSearchParams();
		params.append('page', filterPage);
		params.append('query', filterQuery);
		params.append('periodInitFrom', filterPeriodInitFrom);
		params.append('periodInitTo', filterPeriodInitTo);
		params.append('periodEndFrom', filterPeriodEndFrom);
		params.append('periodEndTo', filterPeriodEndTo);
		params.append('amountFrom', filterAmountFrom);
		params.append('amountTo', filterAmountTo);
		params.append('currency', filterCurrency);
		// Without these the sort silently reset to the default on every filter
		// change, search and page step.
		params.append('column', column);
		params.append('direction', direction);

		return `/AdminScreen/contracts/search?${params.toString()}`;
	};

	const filterFormProps = {
		filterOptions: filterOptions,
		isOpen: isOpen,
		handleDropdownToggle: handleDropdownToggle,
		handleInputChange: handleInputChange,
		clearFilter: () => handleClearFilter(),
		handleApplyFilter: () => handleApplyFilter(),
	};

	const filterBadgeCount = [
		currency !== 'all' && currency !== '',
		Boolean(amountFrom),
		Boolean(amountTo),
		Boolean(periodInitFrom),
		Boolean(periodInitTo),
		Boolean(periodEndFrom),
		Boolean(periodEndTo),
	].filter(Boolean).length;

	// Selection — first-class React state instead of the legacy DOM scrape.
	// `finalizado` rows are not selectable per the handoff.
	const isRowSelectable = (contract) => contractEstado(contract) !== 'finalizado';

	const toggleSelection = (id) => {
		setSelectedIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
	};

	const toggleSelectAll = () => {
		const selectableIds = contracts.filter(isRowSelectable).map((c) => c._id);
		const allSelected = selectableIds.length > 0 && selectableIds.every((id) => selectedIds.includes(id));
		setSelectedIds(allSelected ? [] : selectableIds);
	};

	const selectedContracts = useMemo(
		() => contracts.filter((c) => selectedIds.includes(c._id)),
		[contracts, selectedIds],
	);

	const closeBulkModal = () => setBulkModalMode(null);

	async function handleConfirmAdjust({ mode, value, effectiveFrom }) {
		if (selectedIds.length === 0) {
			toast.error(t('msg.selectAtLeastOneContract'));
			return;
		}
		if (!value) {
			toast.error(t('msg.enterAdjustmentValue'));
			return;
		}
		try {
			dispatch({ type: 'UPDATE_REQUEST' });
			await ContractService.adjustContractsInBulk(selectedIds, { mode, value: Number(value), effectiveFrom });
			toast.success(t('msg.contractsAdjusted'));
			dispatch({ type: 'UPDATE_SUCCESS' });
			setSelectedIds([]);
			closeBulkModal();
		} catch (err) {
			toast.error(getError(err));
			dispatch({ type: 'UPDATE_FAIL' });
		}
	}

	async function handleConfirmClose({ closeDate, closeReason }) {
		if (selectedIds.length === 0) {
			toast.error(t('msg.selectAtLeastOneContract'));
			return;
		}
		if (!closeDate) {
			toast.error(t('msg.enterCloseDate'));
			return;
		}
		try {
			dispatch({ type: 'UPDATE_REQUEST' });
			await ContractService.closeContractsByID(selectedIds, { closeDate, closeReason });
			toast.success(t('msg.contractsClosed'));
			dispatch({ type: 'UPDATE_SUCCESS' });
			setSelectedIds([]);
			closeBulkModal();
		} catch (err) {
			toast.error(getError(err));
			dispatch({ type: 'UPDATE_FAIL' });
		}
	}

	// KPI strip — handoff §3. Stat 1 (Renta mensual total) is the only teal
	// (--color-cat-2) tile; currencies are never summed, always shown as
	// separate lines/sub.
	const statStrips = useMemo(() => {
		if (!stats) return [];
		const rentByCurrency = stats.rentByCurrency || [];
		const uyu = rentByCurrency.find((r) => r._id === 'UYU');
		const usd = rentByCurrency.find((r) => r._id === 'USD');
		const rentValue = uyu
			? `${showCurrency('UYU')} ${uyu.totalAmount.toLocaleString('es-UY')}`
			: <span className='tw:text-ink-3'>—</span>;
		const rentSub = usd ? `+ ${showCurrency('USD')} ${usd.totalAmount.toLocaleString('es-UY')}` : undefined;

		return [
			{
				label: 'Renta mensual total',
				value: rentValue,
				mono: true,
				accent: '--color-cat-2',
				sub: rentSub,
			},
			{
				label: 'Contratos vigentes',
				value: stats.vigentes,
				sub: `${stats.finalizados} finalizados`,
			},
			{
				label: 'Cobros pendientes',
				value: stats.cobrosPendientes,
				subTone: stats.cobrosVencidos > 0 ? 'down' : 'neutral',
				sub: stats.cobrosVencidos > 0 ? `${stats.cobrosVencidos} vencidos` : undefined,
			},
			{
				label: 'Próxima renovación',
				value: stats.nextRenewal ? formatDate(stats.nextRenewal.date) : <span className='tw:text-ink-3'>—</span>,
				small: true,
				mono: true,
				sub: stats.nextRenewal ? stats.nextRenewal.propertyName : undefined,
			},
		];
	}, [stats]);

	const exportFields = [
		{ key: 'customId', label: 'ID', locked: true },
		{ key: 'inmueble', label: 'Inmueble' },
		{ key: 'inquilino', label: 'Inquilino' },
		{ key: 'periodInit', label: 'Inicio' },
		{ key: 'periodEnd', label: 'Fin' },
		{ key: 'amount', label: 'Renta' },
		{ key: 'estado', label: 'Estado' },
	];

	const exportFieldGetters = {
		customId: (contract) => contract.customId || '',
		inmueble: (contract) => contract.property?.name || '',
		inquilino: (contract) => contract.owner?.name || '',
		periodInit: (contract) => formatDate(contract.periodInit),
		periodEnd: (contract) => formatDate(contract.periodEnd),
		amount: (contract) =>
			`${showCurrency(contract.currency) || ''} ${(contract.amount || 0).toLocaleString('es-UY')}`.trim(),
		estado: (contract) => CONTRACT_ESTADO_LABEL[contractEstado(contract)] || '',
	};

	// Must refetch with a big pageSize rather than report=true: the report
	// branch returns raw documents without virtuals or the per-row
	// cobrosPendientes/cobrosVencidos that contractEstado() reads, so the Estado
	// column would come out wrong.
	async function handleExportContracts(selectedKeys) {
		const { data } = await ContractService.search(
			`page=1&query=${query}&column=${column}&direction=${direction}&currency=${currency}&amountFrom=${amountFrom}&amountTo=${amountTo}&periodInitFrom=${periodInitFrom}&periodInitTo=${periodInitTo}&periodEndFrom=${periodEndFrom}&periodEndTo=${periodEndTo}&pageSize=${countContracts || 1}`,
		);
		return buildXlsxExport({
			rows: data.contracts,
			fields: exportFields,
			selectedKeys,
			getters: exportFieldGetters,
			sheetName: 'Alquileres',
			filePrefix: 'alquileres',
		});
	}

	const renderRowActions = (contract) => (
		<RowActionsMenu
			items={[
				canSee() && { label: 'Ver detalles', onClick: () => seeDetails(contract) },
				canEdit() && { label: 'Editar', onClick: () => editContractHandler(contract) },
				canCreate() && { label: 'Agregar cobro', onClick: () => navigate(`/AdminScreen/contracts/newContractPayment?returnTo=${encodeURIComponent(`${window.location.pathname}${search}`)}`) },
				canDelete() && { label: 'Eliminar', onClick: () => deleteContractHandler(contract), danger: true },
			]}
		/>
	);

	const contractColumns = [
		{
			key: 'customId',
			header: '#',
			className: 'tw:w-[140px]',
			render: (contract) => <IdChip>{contract.customId}</IdChip>,
		},
		{
			key: 'inmueble',
			header: 'Inmueble',
			className: 'tw:font-medium tw:max-w-[200px] tw:truncate',
			render: (contract) => contract.property?.name || <span className='tw:text-ink-3'>—</span>,
		},
		{
			key: 'inquilino',
			header: 'Inquilino',
			className: 'tw:text-ink-1 tw:max-w-[160px] tw:truncate',
			render: (contract) =>
				contract.owner ? (
					<a
						href={`/AdminScreen/accounts/account/${contract.owner._id}`}
						onClick={(e) => {
							e.preventDefault();
							e.stopPropagation();
							navigate(`/AdminScreen/accounts/account/${contract.owner._id}`);
						}}
						className='tw:text-ink-1 tw:no-underline tw:hover:underline'
					>
						{contract.owner.name}
					</a>
				) : (
					<span className='tw:text-ink-3'>—</span>
				),
		},
		{
			key: 'periodInit',
			header: 'Inicio',
			sortable: true,
			className: 'tw:font-mono tw:text-[12.5px] tw:text-ink-2',
			render: (contract) => formatDate(contract.periodInit) || <span className='tw:text-ink-4'>—</span>,
		},
		{
			key: 'periodEnd',
			header: 'Fin',
			sortable: true,
			className: 'tw:font-mono tw:text-[12.5px] tw:text-ink-2',
			render: (contract) => formatDate(contract.periodEnd) || <span className='tw:text-ink-4'>—</span>,
		},
		{
			key: 'amount',
			header: 'Renta',
			className: 'tw:text-right tw:font-mono tw:text-[13px] tw:font-medium',
			render: (contract) => `${showCurrency(contract.currency) || ''} ${(contract.amount || 0).toLocaleString('es-UY')}`,
		},
		{
			key: 'estado',
			header: 'Estado',
			render: (contract) => {
				const estado = contractEstado(contract);
				return (
					<Pill tone={CONTRACT_ESTADO_TONE[estado]} dot>
						{CONTRACT_ESTADO_LABEL[estado]}
					</Pill>
				);
			},
		},
	];

	return (
		<div>
			<Helmet>
				<title>{`Lista de ${t('entity.contract.plural')}`}</title>
			</Helmet>

			<FilterForm {...filterFormProps} />

			<ContractDeletionModal
				show={Boolean(deletingContract)}
				record={deletingContract}
				onClose={() => setDeletingContract(null)}
				onDeleted={() => {
					setDeletingContract(null);
					dispatch({ type: 'DELETE_SUCCESS' });
				}}
			/>

			<ContractBulkActionModal
				show={bulkModalMode !== null}
				mode={bulkModalMode || 'adjust'}
				contracts={selectedContracts}
				ipc={ipc}
				onClose={closeBulkModal}
				onConfirmAdjust={handleConfirmAdjust}
				onConfirmClose={handleConfirmClose}
			/>

			<EntityList>
				<PageHead
					title={t('entity.contract.plural')}
					subtitle={stats ? `${stats.vigentes} vigentes · ${countContracts} en total` : `${initialLoading ? '—' : countContracts} en total`}
					actions={
						<>
							<Button variant='default' icon={<FaFileExcel />} onClick={() => setExportModalOpen(true)}>
								Exportar
							</Button>
							{canCreate() && (
								<Button variant='primary' icon={<FaPlus />} onClick={() => navigate('/AdminScreen/newContract')}>
									{`Agregar ${t('entity.contract')}`}
								</Button>
							)}
							{canEdit() && (
								<Button
									icon={<FiRefreshCw />}
									disabled={selectedIds.length === 0}
									title={selectedIds.length === 0 ? 'Seleccioná uno o más alquileres' : undefined}
									onClick={() => setBulkModalMode('adjust')}
								>
									{`Ajustar ${t('entity.contract.plural')}`}
									{selectedIds.length > 0 && <span className='count'>{selectedIds.length}</span>}
								</Button>
							)}
							{canEdit() && (
								<Button
									icon={<FiCheck />}
									disabled={selectedIds.length === 0}
									title={selectedIds.length === 0 ? 'Seleccioná uno o más alquileres' : undefined}
									onClick={() => setBulkModalMode('close')}
								>
									{`Finalizar ${t('entity.contract.plural')}`}
									{selectedIds.length > 0 && <span className='count'>{selectedIds.length}</span>}
								</Button>
							)}
						</>
					}
				/>
				<div className='tw:mb-5'>
					<StatStrip stats={statStrips} loading={initialLoading} />
				</div>
				<ListToolbar
					query={query}
					getFilterUrl={getFilterUrl}
					navigate={navigate}
					searchPlaceholder='Inmueble, inquilino, cuenta…'
					onAdvancedFilter={handleDropdownToggle}
					filterBadgeCount={filterBadgeCount}
					onClearFilters={filterBadgeCount > 0 ? handleClearAdvancedFilters : undefined}
					resultCount={initialLoading ? null : countContracts}
				/>
				<DataTable
					columns={contractColumns}
					rows={contracts}
					loading={loading}
					skeleton
					skeletonRows={pageSize || 15} // 15 matches routes/contractRoutes.js PAGE_SIZE
					error={error}
					sortColumn={column}
					direction={direction}
					onSort={handleSort}
					onRowClick={(contract) => seeDetails(contract)}
					rowActions={canEdit() || canDelete() || canCreate() ? renderRowActions : undefined}
					emptyMessage='No se encontraron contratos con esos filtros.'
					selection={{
						selectedIds,
						onToggle: toggleSelection,
						onToggleAll: toggleSelectAll,
						isSelectable: isRowSelectable,
						bulkBar: selectedIds.length > 0 && (
							<BulkBar
								count={selectedIds.length}
								label={selectedIds.length === 1 ? 'alquiler seleccionado' : 'alquileres seleccionados'}
								onClear={() => setSelectedIds([])}
								actions={
									<>
										<Button size='sm' onClick={() => setBulkModalMode('adjust')} className='tw:bg-white/15 tw:text-white tw:border-white/30'>
											Ajustar
										</Button>
										<Button size='sm' onClick={() => setBulkModalMode('close')} className='tw:bg-white/15 tw:text-white tw:border-white/30'>
											Finalizar
										</Button>
									</>
								}
							/>
						),
					}}
				/>
				<Paginator
					className='tw:mt-4'
					totalCount={countContracts}
					pageSize={pageSize}
					currentPage={parseInt(page)}
					onPageChange={onPageChange}
				/>
			</EntityList>

			<ExportModal
				show={exportModalOpen}
				onClose={() => setExportModalOpen(false)}
				count={countContracts}
				fields={exportFields}
				subtitle={`${t('entity.contract.plural')} — se exportará el listado filtrado actual`}
				onExport={handleExportContracts}
			/>
		</div>
	);
}

export default ContractList;
